// WAAP website Rate Limit (a section on the Access Control tab).
// PUBLIC Open-API surface (root conversant SDK, HMAC) — per-website rate limit:
//   GET    /waap/domains/:domainId/rate-limit  → WaapRateLimit (zeros = none)
//   POST   /waap/domains/:domainId/rate-limit  → upsert the rule
//   DELETE /waap/domains/:domainId/rate-limit  → clear the rule
// :domainId anchors the website; the BE resolves the vendor service from it.
// The public GET returns an all-zeros object when no rule is configured (the
// private surface 404'd); we normalise that to null so the UI shows "Create".
import { useMutation, useQuery, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { waapKeys, WAAP_AREAS, type WaapRateLimit } from './_shared';

const rateLimitPath = (domainId: string) => `/waap/domains/${domainId}/rate-limit`;

/** A rule with no window/limit means "not configured" (public returns zeros). */
const isUnset = (r: WaapRateLimit | null | undefined): boolean =>
  !r || (!r.time_frame && !r.req_limit);

/** Read the website's rate-limit rule (null when none is set). */
export function useWaapRateLimit(domainId: string | undefined) {
  return useQuery({
    queryKey: waapKeys.aspect(domainId ?? '', WAAP_AREAS.websiteRateLimit),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<WaapRateLimit | null> => {
      try {
        const res = await serviceApiV3.get<WaapRateLimit | null>(rateLimitPath(domainId as string));
        return isUnset(res.data) ? null : res.data;
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404) return null;
        throw e;
      }
    },
  });
}

/** Create/update the website's rate-limit rule. Invalidates the read. */
export function useSaveWaapRateLimit(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, WaapRateLimit>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: WaapRateLimit) => {
      const res = await serviceApiV3.post(rateLimitPath(domainId), body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: waapKeys.aspect(domainId, WAAP_AREAS.websiteRateLimit) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Delete the website's rate-limit rule. Invalidates the read. */
export function useDeleteWaapRateLimit(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, void>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await serviceApiV3.delete(rateLimitPath(domainId));
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: waapKeys.aspect(domainId, WAAP_AREAS.websiteRateLimit) });
      options?.onSuccess?.(...args);
    },
  });
}
